import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Sparkles, Lock, Loader2, BookOpen, Users, GraduationCap } from "lucide-react";
import { useAuth, API } from "@/App";
import { fadeInUp, fadeInUpSm } from "@/lib/motion-variants";

const BRAND = "#6C3CE1";
const SITE_URL = "https://cognifyai.online";

/**
 * Public, SEO-indexable page for a single curated flashcard set.
 *
 * Strategy:
 * - Anonymous visitors land here via Google (or a shared link) and see all 5
 *   preview cards as plain server-readable HTML — search-engine friendly.
 * - <Helmet> emits a unique <title>, meta description, canonical, and Open
 *   Graph tags per set, so each page can rank on its own.
 * - The "Unlock all X terms" CTA drives signup; logged-in users go straight
 *   to the dashboard instead.
 */
const PublicSetPage = () => {
  const { setId } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/public/flashcard-sets/${setId}/preview`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setId]);

  const handleUnlock = () => {
    if (user) {
      navigate(user.role === "parent" ? "/parent" : "/dashboard");
    } else {
      login();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-6">
        <Helmet>
          <title>Set not found · Cognify AI</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <BookOpen className="w-12 h-12 text-slate-300 mb-3" />
        <h1 className="text-2xl font-bold text-slate-800">We couldn&apos;t find that set</h1>
        <p className="text-slate-500 mt-2">It may have been removed or the link is incorrect.</p>
        <Link to="/" className="mt-6">
          <Button className="rounded-full" data-testid="public-set-back-home">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Cognify AI
          </Button>
        </Link>
      </div>
    );
  }

  const canonicalUrl = `${SITE_URL}/sets/${data.set_id}`;
  const description =
    `Free ${data.subject || "study"} flashcards: ${data.title}. ` +
    `${data.term_count} terms curated by ${data.curator}. ` +
    `Preview ${data.preview_cards.length} cards free, then sign up to study the full set with AI-powered spaced repetition on Cognify AI.`;
  const keywords = [
    data.title,
    `${data.subject || ""} flashcards`,
    `${data.grade_level || ""} grade ${data.subject || ""}`.trim(),
    "study flashcards",
    "free flashcards",
    "Cognify AI",
  ].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }} data-testid="public-set-page">
      <Helmet>
        <title>{`${data.title} — Free Flashcards · Cognify AI`}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${data.title} — Free Flashcards`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Cognify AI" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${data.title} — Free Flashcards`} />
        <meta name="twitter:description" content={description} />
        {/* JSON-LD structured data — helps Google render rich snippets. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            name: data.title,
            description,
            educationalLevel: data.grade_level,
            learningResourceType: "Flashcard Set",
            inLanguage: "en",
            url: canonicalUrl,
            author: { "@type": "Person", name: data.curator },
            interactionStatistic: {
              "@type": "InteractionCounter",
              interactionType: "https://schema.org/UseAction",
              userInteractionCount: data.study_count,
            },
            publisher: {
              "@type": "Organization",
              name: "Cognify AI",
              url: SITE_URL,
            },
          })}
        </script>
      </Helmet>

      {/* Header */}
      <nav className="border-b border-slate-100 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg text-slate-900" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: BRAND }}>
              <Sparkles className="w-4 h-4" />
            </span>
            Cognify AI
          </Link>
          <Button
            onClick={handleUnlock}
            className="rounded-full text-white"
            style={{ background: BRAND }}
            data-testid="public-set-cta-header"
          >
            {user ? "Open dashboard" : "Sign up free"}
          </Button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <Link to="/" className="text-sm text-slate-500 hover:text-violet-600 inline-flex items-center gap-1 mb-6">
          <ChevronLeft className="w-4 h-4" /> Back to all sets
        </Link>

        {/* Hero */}
        <motion.div {...fadeInUp}>
          <span className="inline-block bg-violet-50 text-violet-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
            {data.subject} · Grade {data.grade_level}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight mb-3" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {data.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-2">
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-violet-500" />
              <strong className="text-slate-900">{data.term_count}</strong> terms
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-emerald-500" />
              <strong className="text-slate-900">{data.study_count.toLocaleString()}</strong> studies
            </span>
            <span className="flex items-center gap-1.5">
              <GraduationCap className="w-4 h-4 text-amber-500" />
              Curated by <strong className="text-slate-900">{data.curator}</strong>
            </span>
          </div>
        </motion.div>

        {/* Preview cards as readable HTML — every card is server-rendered text so SEO crawlers can index it. */}
        <section className="mt-8 space-y-4" aria-label="Free preview cards">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Free preview · {data.preview_cards.length} of {data.term_count} cards
          </h2>
          {data.preview_cards.map((card, i) => (
            <motion.article
              key={`${data.set_id}-${i}`}
              {...fadeInUpSm}
              transition={{ delay: i * 0.05 }}
              className="border-2 border-slate-200 rounded-2xl p-5 bg-white hover:border-violet-300 transition-colors"
              data-testid={`public-card-${i}`}
            >
              <div className="text-xs text-slate-400 font-medium mb-2">Card {i + 1}</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{card.front}</h3>
              <p className="text-base text-slate-700 leading-relaxed">{card.back}</p>
            </motion.article>
          ))}
        </section>

        {/* Locked CTA panel */}
        {data.locked_count > 0 && (
          <motion.section
            {...fadeInUp}
            transition={{ delay: 0.3 }}
            className="mt-10 rounded-3xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-amber-50 px-8 py-10 text-center"
            data-testid="public-set-paywall"
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-white shadow-sm flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-violet-600" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
              {data.locked_count.toLocaleString()} more cards waiting
            </h2>
            <p className="text-slate-600 max-w-lg mx-auto mb-6">
              Sign up free to study the full {data.title} set with AI-powered spaced repetition,
              progress tracking, and personalized quizzes.
            </p>
            <Button
              onClick={handleUnlock}
              size="lg"
              className="rounded-full text-white px-8 h-12"
              style={{ background: BRAND }}
              data-testid="public-set-cta-paywall"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {user ? "Open dashboard" : "Unlock all " + data.term_count + " terms — free"}
            </Button>
            <p className="text-xs text-slate-400 mt-3">No credit card required · Cancel anytime</p>
          </motion.section>
        )}

        {/* Related text — gives crawlers more keyword surface area */}
        <section className="mt-12 prose prose-slate prose-sm max-w-none">
          <h2 className="text-xl font-bold">About this set</h2>
          <p>
            <strong>{data.title}</strong> is a curated {data.subject?.toLowerCase()} flashcard
            collection designed for {data.grade_level && `grade ${data.grade_level} `}students.
            Each of the {data.term_count} terms has been hand-picked by{" "}
            <strong>{data.curator}</strong> to cover the most important concepts you&apos;ll
            encounter on tests and in coursework.
          </p>
          <p>
            Cognify AI uses spaced repetition and AI-powered weak-spot detection to make sure you
            actually retain what you study — not just memorize for one quiz. Sign up free to get
            started with this set and thousands more.
          </p>
        </section>
      </main>

      <footer className="border-t border-slate-100 py-8 mt-16">
        <div className="max-w-5xl mx-auto px-6 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} Cognify AI · <Link to="/" className="hover:text-violet-600">Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default PublicSetPage;
